import { Injectable } from '@angular/core';
import {
  ParameterDefinition,
  ParameterHelper,
  ParameterType,
  ParameterValue,
  ParameterValuesContainer,
} from '@barthauer/abc/queries/parameters/core';
import { ObjectHelper } from 'libs/abc/core/helpers';
import { AbcCoreDataModule } from './abc-core-data.module';
import { ICoreDataEntity } from './models/dataEntity.model';
import { QueryBase, QueryObjectsModel } from './models/query.model';
import { ObjectSpaceService } from './object-space.service';

@Injectable({
  providedIn: AbcCoreDataModule,
})
export class QueryHelper {
  constructor() {}

  public static ensureQueryParameterValues(query: ParameterValuesContainer, parameters: ParameterValue[]): void {
    if (parameters?.length) {
      if (!query.Parameters) {
        query.Parameters = [];
      }
      ParameterHelper.ensureParameterValues(query.Parameters, parameters);
    }
  }

  public static findParametersInUse(
    query: QueryObjectsModel,
    parameters?: ParameterDefinition[]
  ): ParameterDefinition[] {
    if (!parameters) {
      parameters = query?.ParameterDefinitions;
    }
    const result: ParameterDefinition[] = [];
    if (query?.ObjectQueries?.length && parameters?.length) {
      for (const p of parameters) {
        if (this.isParameterInUse(query.ObjectQueries, p)) {
          result.push(p);
        }
      }
    }
    return result;
  }

  private static isParameterInUse(queries: QueryBase[], p: ParameterDefinition) {
    if (queries?.length) {
      for (const q of queries) {
        if (this.isParameterNameInExpression(q.OPath, p.Name)) {
          return true;
        }
        if (q.Columns?.length) {
          for (const c of q.Columns) {
            if (this.isParameterNameInExpression(c.OPath, p.Name)) {
              return true;
            }
          }
        }
        if (this.isParameterInUse(q.ObjectQueries, p)) {
          return true;
        }
      }
    }
    return false;
  }

  public static isParameterNameInExpression(opath: string, parameterName: string) {
    const regexpString = '^.*?(\\$' + parameterName + ')W*?.*?$';
    if (parameterName && opath?.match(new RegExp(regexpString))?.length) {
      return true;
    }
    return false;
  }

  public static async applyObjectFilterToQuery(
    objectSpace: ObjectSpaceService,
    query: QueryObjectsModel,
    entities: ICoreDataEntity[]
  ): Promise<void> {
    if (entities?.length && query?.ObjectQueries?.length) {
      const idsByObjectType: Map<string, string[]> = new Map();
      for (const entity of entities) {
        const ot = await objectSpace.getObjectType(entity.ObjectType).toPromise();
        let currentOt = ot;
        while (currentOt) {
          const key = this.getNormalizedName(currentOt.name);
          let ids = idsByObjectType.get(key);
          if (!ids) {
            ids = [];
            idsByObjectType.set(key, ids);
          }
          ids.push(entity.Id);
          currentOt = currentOt.baseType;
        }
      }

      for (const e of ObjectHelper.asArray(idsByObjectType)) {
        const otkey = e[0];
        const ids = e[1];
        const existingParameters = this.fillMatchingParametersIfAvailable(query, otkey, ids);

        let queriesToModify = query.ObjectQueries.filter(
          (q) =>
            (q.ObjectType && this.getNormalizedName(q.ObjectType) === otkey) ||
            (!q.ObjectType &&
              this.getNormalizedName(
                q.OPath?.trim().replace(' ', '').replace('\t', '').replace('\n', '').replace('\r', '')
              )?.startsWith('type(') +
                otkey +
                ')')
        );

        if (existingParameters?.length) {
          queriesToModify = queriesToModify.filter((q) =>
            existingParameters.find((p) => !this.isParameterNameInExpression(q.OPath, p.Name))
          );
        }

        if (queriesToModify.length) {
          let parameterName = 'Selection__' + otkey;
          let newOPathQueryCondition: string;
          if (!query.Parameters) {
            query.Parameters = [];
          }
          let pv = query.Parameters.find((_) => _.Name === parameterName);
          if (!pv) {
            pv = new ParameterValue(parameterName);
            query.Parameters.push(pv);
          }
          pv.Value = ids;
          newOPathQueryCondition = ObjectHelper.nameof<ICoreDataEntity>('Id') + ' In ($' + pv.Name + ')';

          for (const q of queriesToModify) {
            if (q.OPath) {
              q.OPath = newOPathQueryCondition + ' And (' + q.OPath + ')';
            } else {
              q.OPath = newOPathQueryCondition;
            }
          }
        }
      }
    }
  }

  private static getNormalizedName(name: string) {
    return ParameterHelper.getNormalizedName(name);
  }

  private static fillMatchingParametersIfAvailable(
    query: QueryObjectsModel,
    objectTypeKey: string,
    ids: string[]
  ): ParameterDefinition[] {
    let result = this.fillMatchingParametersIfAvailableCore(query, objectTypeKey, ids, true);
    if (!result.length) {
      result = this.fillMatchingParametersIfAvailableCore(query, objectTypeKey, ids, false);
    }
    return result;
  }

  private static fillMatchingParametersIfAvailableCore(
    query: QueryObjectsModel,
    objectTypeKey: string,
    ids: string[],
    multiSelect: boolean
  ): ParameterDefinition[] {
    const result: ParameterDefinition[] = [];

    if (query.ParameterDefinitions) {
      for (const pd of query.ParameterDefinitions) {
        if (pd.Type === ParameterType.DataAccessByObjectType) {
          if (
            ((pd.AllowMultiSelect && multiSelect) || (!pd.AllowMultiSelect && !multiSelect)) &&
            this.getNormalizedName(pd.DataArgument) === objectTypeKey
          ) {
            pd.IsFixed = true;
            pd.Value = ids;
            if (!query.Parameters) {
              query.Parameters = [];
            }
            ParameterHelper.ensureParameterValues(query.Parameters, [{ Name: pd.Name, Value: pd.Value }]);
            result.push(pd);
          }
        }
      }
    }

    return result;
  }
}
